import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import Map, { Layer, MapRef, Marker, Popup, Source } from "react-map-gl";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  CommunityBoundary,
  MonitoringAddress,
  MonitoringMode,
} from "src/Types/monitoring.types";
import {
  MonitoringAlert,
  MonitoringListingSummary,
} from "src/Types/alerts.types";
import mapboxgl from "src/utils/mapboxGl";
import { formatDate, formatLongDuration } from "src/i18n/format";

const AIRBNB_MARKER = "#f43f5e";
const VRBO_MARKER = "#f59e0b";
const NEARBY_MARKER = "#38bdf8";

const listingProvider = (listing: MonitoringListingSummary | null | undefined) =>
  listing?.provider === "VRBO" ? "VRBO" : "AIRBNB";

const markerColors = (alert: MonitoringAlert) => {
  const nearby = alert.listing?.matchStatus === "NEARBY";
  const isVrbo = listingProvider(alert.listing) === "VRBO";
  if (nearby) {
    return {
      fill: NEARBY_MARKER,
      glow: "0 0 0 5px rgba(56, 189, 248, 0.2)",
      dashed: true,
    };
  }
  if (isVrbo) {
    return {
      fill: alert.type === "NEW_LISTING" ? VRBO_MARKER : "#d97706",
      glow: "0 0 0 5px rgba(245, 158, 11, 0.22)",
      dashed: false,
    };
  }
  return {
    fill: alert.type === "NEW_LISTING" ? AIRBNB_MARKER : "#f59e0b",
    glow: "0 0 0 5px rgba(244, 63, 94, 0.18)",
    dashed: false,
  };
};

interface MonitoringMapProps {
  mode: MonitoringMode;
  community: CommunityBoundary | null;
  addresses: MonitoringAddress[];
  alerts: MonitoringAlert[];
  selectedAlertId: string | null;
  onSelectAlert: (alert: MonitoringAlert | null) => void;
  fullBleed?: boolean;
}

interface MonitoringFocusOptions {
  mode: MonitoringMode;
  community: CommunityBoundary | null;
  addresses: MonitoringAddress[];
  fullBleed: boolean;
  duration?: number;
}

export const listingPopupMetadata = (
  listing: MonitoringListingSummary,
  t: TFunction
) => {
  const host = listing.hostName || listing.hostId;
  const rating = listing.ratingLabel || listing.rating;
  return [
    ...(host ? [{ label: t("dashboard.metaHost"), value: host }] : []),
    ...(listing.price
      ? [{ label: t("dashboard.metaPrice"), value: listing.price }]
      : []),
    ...(rating ? [{ label: t("dashboard.metaRating"), value: rating }] : []),
    {
      label: t("dashboard.metaLastObserved"),
      value: formatDate(listing.lastSeenAt),
    },
    {
      label: t("dashboard.metaListedFor"),
      value: formatLongDuration(
        t,
        listing.currentAppearanceStartedAt,
        listing.active ? undefined : listing.lastSeenAt
      ),
    },
    {
      label: t("dashboard.metaTimesListed"),
      value: String(listing.appearanceCount),
    },
  ];
};

export const focusMonitoringMap = (
  map: Pick<MapRef, "fitBounds" | "flyTo">,
  {
    mode,
    community,
    addresses,
    fullBleed,
    duration = 900,
  }: MonitoringFocusOptions
) => {
  if (mode === "COMMUNITY" && community) {
    map.fitBounds(
      [
        [community.bounds[0], community.bounds[1]],
        [community.bounds[2], community.bounds[3]],
      ],
      {
        padding: 64,
        duration,
        maxZoom: 13,
      }
    );
    return;
  }

  if (mode !== "ADDRESSES" || addresses.length === 0) return;

  if (fullBleed || addresses.length === 1) {
    map.flyTo({
      center: [addresses[0].longitude, addresses[0].latitude],
      zoom: 15,
      duration,
    });
    return;
  }

  const longitudes = addresses.map((address) => address.longitude);
  const latitudes = addresses.map((address) => address.latitude);
  map.fitBounds(
    [
      [Math.min(...longitudes), Math.min(...latitudes)],
      [Math.max(...longitudes), Math.max(...latitudes)],
    ],
    {
      padding: 80,
      duration,
      maxZoom: 15,
    }
  );
};

export const focusSelectedListing = (
  map: Pick<MapRef, "flyTo">,
  listing: { longitude: number; latitude: number },
  duration = 700
) => {
  map.flyTo({
    center: [listing.longitude, listing.latitude],
    zoom: 15,
    duration,
    padding: { top: 360, bottom: 80, left: 180, right: 180 },
  });
};

const MonitoringMap = React.forwardRef<HTMLDivElement, MonitoringMapProps>(
  (
    {
      mode,
      community,
      addresses,
      alerts,
      selectedAlertId,
      onSelectAlert,
      fullBleed = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const mapRef = useRef<MapRef>(null);

    const focusMonitoringArea = useCallback((duration = 900) => {
      const map = mapRef.current;
      if (!map) return;
      focusMonitoringMap(map, {
        mode,
        community,
        addresses,
        fullBleed,
        duration,
      });
    }, [addresses, community, fullBleed, mode]);

    useEffect(() => {
      focusMonitoringArea();
    }, [focusMonitoringArea]);

    useEffect(() => {
      const selectedAlert = alerts.find((alert) => alert.id === selectedAlertId);
      if (!selectedAlert?.listing || !mapRef.current) return;
      focusSelectedListing(mapRef.current, selectedAlert.listing);
    }, [alerts, selectedAlertId]);

  const selectedMapAlert = alerts.find(
    (alert) => alert.id === selectedAlertId && alert.listing
  );

  const boundaryFeature = community
    ? {
        type: "Feature" as const,
        properties: {},
        geometry: community.geometry,
      }
    : null;

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        width: "100%",
        height: fullBleed ? "100%" : { xs: 420, md: "calc(100vh - 144px)" },
        minHeight: fullBleed ? 0 : { md: 620 },
        overflow: "hidden",
        borderRadius: fullBleed ? { xs: 0, md: 3 } : { xs: 3, md: 4 },
        border: fullBleed ? { xs: 0, md: "1px solid" } : "1px solid",
        borderColor: "divider",
        bgcolor: "#111827",
        "& .monitoring-listing-popup .mapboxgl-popup-content": {
          p: 0,
          borderRadius: 3,
          bgcolor: "#0f172a",
          color: "#e2e8f0",
          border: "1px solid rgba(148, 163, 184, 0.28)",
          boxShadow: "0 18px 50px rgba(0, 0, 0, 0.5)",
          overflow: "hidden",
        },
        "& .monitoring-listing-popup .mapboxgl-popup-tip": {
          borderTopColor: "#0f172a",
          borderBottomColor: "#0f172a",
        },
      }}
    >
      <Map
        ref={mapRef}
        mapLib={mapboxgl as never}
        onLoad={() => focusMonitoringArea(0)}
        initialViewState={{
          longitude: -121.9233,
          latitude: 36.5552,
          zoom: 11,
        }}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ""}
        mapStyle="mapbox://styles/solarstar101/clabl7lkj005u15ocmh23ghmq"
        style={{ width: "100%", height: "100%" }}
        dragRotate={false}
        attributionControl
        preserveDrawingBuffer
        onClick={() => onSelectAlert(null)}
      >
        {mode === "COMMUNITY" && boundaryFeature && (
          <Source id="community-boundary" type="geojson" data={boundaryFeature}>
            <Layer
              id="community-boundary-fill"
              type="fill"
              paint={{
                "fill-color": "#8b5cf6",
                "fill-opacity": 0.25,
              }}
            />
            <Layer
              id="community-boundary-line"
              type="line"
              paint={{
                "line-color": "#c4b5fd",
                "line-width": 3,
              }}
            />
          </Source>
        )}

        {mode === "ADDRESSES" &&
          addresses.map((address, index) => (
            <Marker
              key={address.id || `${address.longitude}-${address.latitude}`}
              longitude={address.longitude}
              latitude={address.latitude}
              anchor="bottom"
            >
              <Box
                aria-label={t("dashboard.map.addressMarker", { index: index + 1, label: address.label })}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50% 50% 50% 8px",
                  transform: "rotate(-45deg)",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "primary.main",
                  color: "white",
                  border: "3px solid white",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    transform: "rotate(45deg)",
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>
            </Marker>
          ))}

        {alerts
          .filter((alert) => alert.listing)
          .map((alert) => {
            const nearby = alert.listing!.matchStatus === "NEARBY";
            const isVrbo = listingProvider(alert.listing) === "VRBO";
            const disclaimer = isVrbo
              ? t("dashboard.nearbyDisclaimerVrbo")
              : t("dashboard.nearbyDisclaimerAirbnb");
            const colors = markerColors(alert);
            return (
              <Marker
                key={`alert-${alert.id}`}
                longitude={alert.listing!.longitude}
                latitude={alert.listing!.latitude}
                anchor="center"
                onClick={(event) => {
                  event.originalEvent.stopPropagation();
                  onSelectAlert(alert);
                }}
              >
                <Tooltip
                  title={
                    nearby
                      ? disclaimer
                      : `${alert.title} — ${t("dashboard.matchInside")}`
                  }
                  arrow
                >
                  <Box
                    aria-label={
                      nearby
                        ? t(
                            isVrbo
                              ? "dashboard.alertAriaNearbyVrbo"
                              : "dashboard.alertAriaNearbyAirbnb",
                            { title: alert.title, disclaimer }
                          )
                        : t(
                            isVrbo
                              ? "dashboard.alertAriaVrbo"
                              : "dashboard.alertAriaAirbnb",
                            { title: alert.title }
                          )
                    }
                    sx={{
                      width: selectedAlertId === alert.id ? 22 : 16,
                      height: selectedAlertId === alert.id ? 22 : 16,
                      borderRadius: "50%",
                      bgcolor: colors.fill,
                      border: colors.dashed
                        ? "3px dashed white"
                        : "3px solid white",
                      boxShadow: colors.glow,
                      cursor: "pointer",
                      transition: "width 0.2s, height 0.2s",
                    }}
                  />
                </Tooltip>
              </Marker>
            );
          })}

        {selectedMapAlert?.listing && (
          <Popup
            className="monitoring-listing-popup"
            longitude={selectedMapAlert.listing.longitude}
            latitude={selectedMapAlert.listing.latitude}
            anchor="bottom"
            offset={18}
            maxWidth="320px"
            closeOnClick={false}
            closeButton={false}
            onClose={() => onSelectAlert(null)}
          >
            <Box sx={{ width: { xs: 270, sm: 310 }, position: "relative" }}>
              <IconButton
                aria-label={t("dashboard.closeListing")}
                size="small"
                onClick={() => onSelectAlert(null)}
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  top: 8,
                  right: 8,
                  color: "white",
                  bgcolor: "rgba(2, 6, 23, 0.72)",
                  "&:hover": { bgcolor: "rgba(2, 6, 23, 0.9)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              {selectedMapAlert.listing.pictureUrl && (
                <Box
                  component="img"
                  src={selectedMapAlert.listing.pictureUrl}
                  alt=""
                  sx={{ width: "100%", height: 112, objectFit: "cover" }}
                />
              )}
              <Stack spacing={1.25} sx={{ p: 2 }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={
                      listingProvider(selectedMapAlert.listing) === "VRBO"
                        ? t("dashboard.providerVrbo")
                        : t("dashboard.providerAirbnb")
                    }
                    sx={{
                      alignSelf: "flex-start",
                      bgcolor:
                        listingProvider(selectedMapAlert.listing) === "VRBO"
                          ? "rgba(245, 158, 11, 0.2)"
                          : "rgba(244, 63, 94, 0.2)",
                      color:
                        listingProvider(selectedMapAlert.listing) === "VRBO"
                          ? VRBO_MARKER
                          : AIRBNB_MARKER,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    size="small"
                    color={
                      selectedMapAlert.listing.matchStatus === "NEARBY"
                        ? "info"
                        : "success"
                    }
                    label={
                      selectedMapAlert.listing.matchStatus === "NEARBY"
                        ? t("dashboard.matchNearby", {
                            distance:
                              selectedMapAlert.listing.boundaryDistanceMiles?.toFixed(
                                2
                              ) || "≤0.25",
                          })
                        : t("dashboard.matchInside")
                    }
                    sx={{ alignSelf: "flex-start" }}
                  />
                </Stack>
                <Box>
                  <Typography fontWeight={800} lineHeight={1.25}>
                    {selectedMapAlert.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedMapAlert.listing.roomType
                      ? `${selectedMapAlert.listing.roomType} · `
                      : ""}
                    {t("dashboard.map.listingId", {
                      id: selectedMapAlert.listing.providerListingId,
                    })}
                  </Typography>
                </Box>
                <Divider />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                  }}
                >
                  {listingPopupMetadata(selectedMapAlert.listing, t).map((item) => (
                    <Box key={item.label} minWidth={0}>
                      <Typography
                        sx={{
                          color: "#64748b",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: "0.72rem" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {selectedMapAlert.listing.latitude.toFixed(5)},{" "}
                  {selectedMapAlert.listing.longitude.toFixed(5)}
                </Typography>
                {selectedMapAlert.listing.matchStatus === "NEARBY" && (
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: "rgba(56, 189, 248, 0.1)",
                      color: "#bae6fd",
                      fontSize: "0.68rem",
                      lineHeight: 1.45,
                    }}
                  >
                    {listingProvider(selectedMapAlert.listing) === "VRBO"
                      ? t("dashboard.nearbyDisclaimerShortVrbo")
                      : t("dashboard.nearbyDisclaimerShortAirbnb")}
                  </Box>
                )}
                <Button
                  component="a"
                  href={selectedMapAlert.listing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={
                    listingProvider(selectedMapAlert.listing) === "VRBO"
                      ? {
                          bgcolor: VRBO_MARKER,
                          "&:hover": { bgcolor: "#d97706" },
                        }
                      : undefined
                  }
                >
                  {listingProvider(selectedMapAlert.listing) === "VRBO"
                    ? t("dashboard.ctaViewVrbo")
                    : t("dashboard.ctaViewAirbnb")}
                </Button>
              </Stack>
            </Box>
          </Popup>
        )}
      </Map>

      {!fullBleed && <Chip
        icon={<PlaceIcon />}
        label={
          mode === "COMMUNITY"
            ? community?.name.split(",")[0] || t("dashboard.map.selectCommunity")
            : t("dashboard.toolbar.monitoredAddresses", { count: addresses.length })
        }
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          maxWidth: "calc(100% - 32px)",
          bgcolor: "rgba(15, 23, 42, 0.92)",
          backdropFilter: "blur(12px)",
          fontWeight: 700,
        }}
      />}

      {mode === "COMMUNITY" && community && (
        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 28,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: "rgba(15, 23, 42, 0.85)",
            fontSize: 11,
          }}
        >
          {t("dashboard.map.boundaryData")}{" "}
          <Link
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer"
            color="inherit"
          >
            {t("dashboard.map.osmContributors")}
          </Link>
        </Box>
      )}
    </Box>
  );
});

MonitoringMap.displayName = "MonitoringMap";

export default MonitoringMap;
